import type { AvatarConfig } from "../avatar/types";
import axios, { configHeader } from "./axios";

/** Shapes from docs/AVATAR-CONTRACT-v2.md. */
export interface AvatarPhoto {
  id: number;
  url: string;
  createdAt?: string;
}

/** Public rendering data; excludes upload history and storage identifiers. */
export interface AvatarSummary {
  mode: "character" | "photo" | null;
  config: AvatarConfig | null;
  photo: { url: string } | null;
}

export interface AvatarResponse extends AvatarSummary {
  mode: "character" | "photo" | null;
  /** Last character config — kept even while a photo is active. */
  config: AvatarConfig | null;
  /** Active photo when mode === 'photo'. */
  photo: { id: number; url: string } | null;
  /** Upload history, newest first, at most MAX_AVATAR_PHOTOS. */
  photos: AvatarPhoto[];
  updatedAt: string | null;
}

export const MAX_AVATAR_PHOTOS = 5;

export const EMPTY_AVATAR: AvatarResponse = { mode: null, config: null, photo: null, photos: [], updatedAt: null };

const BASE = "/user/me/avatar";

export const getMyAvatar = async (): Promise<AvatarResponse> =>
  (await axios.get<AvatarResponse>(BASE, configHeader())).data;

export const putCharacter = async (config: AvatarConfig): Promise<AvatarResponse> =>
  (await axios.put<AvatarResponse>(BASE, { mode: "character", config }, configHeader())).data;

export const choosePhoto = async (photoId: number): Promise<AvatarResponse> =>
  (await axios.put<AvatarResponse>(BASE, { mode: "photo", photoId }, configHeader())).data;

/** Upload a client-prepared data URL; server stores it, makes it active and trims history to 5. */
export const uploadPhoto = async (dataUrl: string): Promise<AvatarResponse> =>
  (await axios.post<AvatarResponse>(`${BASE}/photos`, { image: dataUrl }, configHeader())).data;

export const deletePhoto = async (photoId: number): Promise<AvatarResponse> =>
  (await axios.delete<AvatarResponse>(`${BASE}/photos/${photoId}`, configHeader())).data;

/** Back to initials (mode=NULL); config and photo history are kept server-side. */
export const resetAvatar = async (): Promise<AvatarResponse> =>
  (await axios.delete<AvatarResponse>(BASE, configHeader())).data;

export const avatarApi = {
  get: getMyAvatar,
  putCharacter,
  choosePhoto,
  uploadPhoto,
  deletePhoto,
  reset: resetAvatar,
};
